import t from "@/lang/lang";
import { ReactNode, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { FilterMode } from "@/shared/lib/filter/filter";
import { formatDate } from "@/shared/lib/utilities/date";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { DocumentReferenceNumberById } from "@/services/backend/documents/components/document-reference-number-by-id";
import { Document } from "@/services/backend/documents/types/document";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { useCustomersServiceObjectDeleteMutation } from "@/services/backend/customers/api/service-object-api";
import { useDocumentsListQuery } from "@/services/backend/documents/api/document-api";
import { UUID } from "@bitory-gmbh/nidavellir";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { toast } from "sonner";

export function DeleteServiceObjectDialog({
  serviceObjectId,
  trigger,
  onDeleted,
}: {
  serviceObjectId: string;
  trigger?: ReactNode;
  onDeleted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [deleteServiceObject, { isLoading, error, reset }] =
    useCustomersServiceObjectDeleteMutation();

  const onConfirm = () => {
    if (isLoading) {
      return;
    }
    deleteServiceObject({ id: serviceObjectId })
      .unwrap()
      .then(() => {
        toast.success(t("Service Objekt gelöscht"));
        reset();
        setOpen(false);
        onDeleted();
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button type="button" variant="destructive">
            <Trash2 />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Service Objekt wirklich löschen?")}</DialogTitle>
          <DialogDescription>
            {t(
              "Das Service Objekt wird gelöscht. Informationen wie der Name und die Adresse gehen dabei verloren. Die unten aufgeführten Dokumente selbst bleiben unverändert erhalten.",
            )}
          </DialogDescription>
        </DialogHeader>
        <DocumentsTable serviceObjectId={serviceObjectId} />
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isLoading}>
              {t("Abbrechen")}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocumentsTable({ serviceObjectId }: { serviceObjectId: UUID }) {
  const { data, error, isLoading } = useDocumentsListQuery({
    serviceObject: {
      active: true,
      mode: FilterMode.Or,
      values: [serviceObjectId],
    },
    limit: 10,
  });

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("Dokumentnummer")}</TableHead>
            <TableHead>{t("Erstellt am")}</TableHead>
            <TableHead>{t("Bearbeitet am")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {error && <TableRtkErrorRow colSpan={3} error={error} />}
          {isLoading && <TableLoadingRow colSpan={3} />}
          {data && data.items.length === 0 && (
            <TableEmptyRow colSpan={3} text={t("Keine Dokumente")} />
          )}
          {data &&
            data.items.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell>
                  <DocumentNumber document={doc} />
                </TableCell>
                <TableCell>{formatDate(doc.createdAt)}</TableCell>
                <TableCell>{formatDate(doc.updatedAt)}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {data && data.totalCount - data.count > 0 && (
        <p className="text-muted-foreground text-sm">
          {t("+ {n} weitere Dokumente").replace(
            "{n}",
            String(data.totalCount - data.count),
          )}
        </p>
      )}
    </div>
  );
}

/**
 * DocumentNumber displays the reference number of a listed document. The
 * document list does not carry the service kind, so it is derived from the
 * process of the document.
 */
function DocumentNumber({ document }: { document: Document }) {
  const { data: process, isLoading } = useProcessesProcessShowQuery(
    { id: document.processId! },
    { skip: !document.processId },
  );

  if (isLoading) {
    return <Skeleton className="h-2 w-52" />;
  }

  if (!process) {
    return <DocumentNumberById documentId={document.id} />;
  }

  return (
    <DocumentReferenceNumberById
      serviceKind={process.serviceKind}
      documentKind={document.documentKind}
      documentId={document.id}
    />
  );
}
